import { useState } from "react";
import type { CarbonProject } from "@/mocks/carbono";
import CarbonStatusBadge from "./CarbonStatusBadge";

interface CarbonProjectTableProps {
  projects: CarbonProject[];
  onOpen: (project: CarbonProject) => void;
}

export default function CarbonProjectTable({ projects, onOpen }: CarbonProjectTableProps) {
  const [sortKey, setSortKey] = useState<string>("co2eEstimated");
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = [...projects].sort((a, b) => {
    const av = a[sortKey as keyof CarbonProject] as number;
    const bv = b[sortKey as keyof CarbonProject] as number;
    return sortAsc ? (av > bv ? 1 : -1) : (av > bv ? -1 : 1);
  });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const SortIcon = ({ colKey }: { colKey: string }) => {
    if (sortKey !== colKey) return <i className="ri-arrow-up-down-line w-3 h-3 flex items-center justify-center text-foreground-300 ml-1" />;
    return sortAsc
      ? <i className="ri-arrow-up-line w-3 h-3 flex items-center justify-center text-primary-600 ml-1" />
      : <i className="ri-arrow-down-line w-3 h-3 flex items-center justify-center text-primary-600 ml-1" />;
  };

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-background-50 border-b border-background-200">
              <th className="text-left px-4 py-3 font-semibold text-foreground-700">Projeto</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700">Tipo</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700">Município/UF</th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("areaHa")}>
                <span className="inline-flex items-center">Área ha <SortIcon colKey="areaHa" /></span>
              </th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("treesCount")}>
                <span className="inline-flex items-center">Árvores <SortIcon colKey="treesCount" /></span>
              </th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("biomassEstimated")}>
                <span className="inline-flex items-center">Biomassa <SortIcon colKey="biomassEstimated" /></span>
              </th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("carbonEstimated")}>
                <span className="inline-flex items-center">Carbono <SortIcon colKey="carbonEstimated" /></span>
              </th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("co2eEstimated")}>
                <span className="inline-flex items-center">CO2e <SortIcon colKey="co2eEstimated" /></span>
              </th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("confidenceLevel")}>
                <span className="inline-flex items-center">Confiança <SortIcon colKey="confidenceLevel" /></span>
              </th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700">Status</th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {sorted.map((p) => (
              <tr key={p.id} className="hover:bg-background-50/50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground-800">{p.name}</div>
                  <div className="text-foreground-400 text-[11px]">{p.client}</div>
                </td>
                <td className="px-4 py-3 text-foreground-700">{p.type}</td>
                <td className="px-4 py-3 text-foreground-700">{p.municipality}/{p.uf}</td>
                <td className="px-4 py-3 text-right text-foreground-700">{p.areaHa}</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{p.treesCount.toLocaleString("pt-BR")}</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{p.biomassEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{p.carbonEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t</td>
                <td className="px-4 py-3 text-right font-semibold text-primary-700">{p.co2eEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-semibold ${p.confidenceLevel >= 80 ? "text-primary-700" : p.confidenceLevel >= 60 ? "text-caution-700" : "text-warning-700"}`}>
                    {p.confidenceLevel}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <CarbonStatusBadge status={p.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onOpen(p)}
                      className="w-7 h-7 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors"
                      title="Abrir"
                    >
                      <i className="ri-eye-line w-full h-full flex items-center justify-center text-foreground-500" />
                    </button>
                    <button className="w-7 h-7 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors" title="Mapa">
                      <i className="ri-map-pin-line w-full h-full flex items-center justify-center text-foreground-500" />
                    </button>
                    <button className="w-7 h-7 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors" title="Relatório">
                      <i className="ri-file-chart-line w-full h-full flex items-center justify-center text-foreground-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}